import React, { useState, useContext, useRef } from "react";
import {
  BasicFolderData,
  Folder,
  hierarchyEntitiesType,
  ShortCutType,
  SourceEntityToMoveType
} from "./types";
import { PhotoManagerContext } from "./index";
import { useFolderData, getUrlParams } from "./BreadcrumbsUtilities";
import { useCreateAction } from "./useCreateAction";
import CreateDropdown from "./CreateActionDropdown";
const GreenArrow = require("../styles/images/GreenArrow.svg");
const arrowDownIcon = require("../styles/images/white-down-arrow.svg");
import { usePopupClickOutside } from "../../../hooks/usePopupClickOutside";
import Rename from "../components/FolderActions/Rename";
import Delete from "../components/FolderActions/Delete";
import DeleteShortcut from "./FolderFeatures/DeleteShortcut";
import { updateFolder, deleteFolder } from "../API/api";
import MoveToFolderPopup from "../components/MoveToFolderPopup";
const shortcutIcon = require("../styles/images/shortcut-icon.svg");
const folderIcon = require("../styles/images/folder-icon.svg");
const editIcon = require("../styles/images/edit.svg");
const submitIcon = require("../../ManagePrepaidCustomers/components/images/submit.svg");
const cancelIcon = require("../../ManagePrepaidCustomers/components/images/cancel.svg");
import { useEditEntityName } from "./useEditEntityName";

interface FolderViewBreadcrumbProps {
  hierarchy?: hierarchyEntitiesType[];
}

const FolderViewBreadcrumb: React.FC<FolderViewBreadcrumbProps> = ({}) => {
  const context = useContext(PhotoManagerContext);
  const popUpRef = useRef<HTMLDivElement>(null);
  const { referenceId, folderId } = getUrlParams();
  const { activeFolder, isLoadingFolder, fetchFolderData } = useFolderData(
    folderId,
    referenceId
  );
  const [showHiddenLinks, setShowHiddenLinks] = useState<boolean>(false);
  const [showOrganizeOptns, setShowOrganizeOptns] = useState<boolean>(false);
  const [showRenameModal, setShowRenameModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showDeleteShortcutModal, setShowDeleteShortcutModal] =
    useState<boolean>(false);
  const [showMovePopup, setShowMovePopup] = useState<boolean>(false);
  const [srcEntity, setSrcEntity] = useState<SourceEntityToMoveType | null>(
    null
  );

  const {
    isInEditMode,
    entityNameDraft,
    toggleEditMode,
    updateDraftName,
    handleKeyDownAction,
    submitNameChange,
    hasInvalidatedValues
  } = useEditEntityName();

  const [displayName, setDisplayName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  usePopupClickOutside(popUpRef, () => {
    setShowHiddenLinks(false);
    setShowOrganizeOptns(false);
  });

  const handleBreadcrumbClick = () => {
    context?.navigateToRoot?.();
    window.history.pushState({}, "", "/library");
    context?.setSelectedView(null);
  };

  const { handleGalleryClick, handleFolderClick } = useCreateAction();

  const isDeeplyNested = activeFolder && activeFolder.hierarchy?.length > 2;
  const hiddenLinks = activeFolder && activeFolder.hierarchy?.slice(0, -2);
  const shownLinks =
    activeFolder && activeFolder.hierarchy?.slice(-2).slice(0, -1);

  const basicFolderData = activeFolder
    ? {
        id: activeFolder?.id,
        name: activeFolder?.name,
        parentFolderId: activeFolder?.parentFolderId
      }
    : null;

  const url = new URL(window.location.href);
  const folder =
    referenceId && activeFolder
      ? { ...activeFolder, referenceId: referenceId }
      : null;

  const renderParentLinks = () => {
    if (isDeeplyNested) {
      return (
        <>
          <div className="deeply-nested-view">
            <button
              type="button"
              className="green-text geo-text-btn"
              onClick={() => setShowHiddenLinks(!showHiddenLinks)}
            >
              ...
            </button>
            {showHiddenLinks && (
              <div className="hidden-links-card">
                {hiddenLinks?.map((link: hierarchyEntitiesType) => {
                  return (
                    <div>
                      <button
                        type="button"
                        className="hidden-links geo-text-btn text-limit black-text"
                        onClick={() =>
                          context?.handleBreadcrumbClick(
                            link.id,
                            link.entityType,
                            link.entityType === "LibraryFolderReference"
                              ? "folder"
                              : undefined
                          )
                        }
                      >
                        {link.entityType === "LibraryFolderReference" ? (
                          <img src={shortcutIcon} />
                        ) : (
                          <img src={folderIcon} />
                        )}

                        {link.name}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <span className="grey-text bc-links-divider"> / </span>
          {shownLinks?.map((parent: hierarchyEntitiesType) => {
            return (
              <>
                <button
                  type="button"
                  className="green-text geo-text-btn text-limit-w"
                  onClick={() =>
                    context?.handleBreadcrumbClick(
                      parent.id,
                      parent.entityType,
                      parent.entityType === "LibraryFolderReference"
                        ? "folder"
                        : undefined
                    )
                  }
                >
                  {parent.entityType === "LibraryFolderReference" && (
                    <img src={shortcutIcon} />
                  )}
                  {parent.name}
                </button>
                <span className="grey-text bc-links-divider"> / </span>
              </>
            );
          })}
        </>
      );
    }
    return (
      <>
        {activeFolder?.hierarchy
          ?.slice(0, -1)
          .map((parent: hierarchyEntitiesType) => {
            return (
              <>
                <button
                  type="button"
                  className="green-text geo-text-btn text-limit-w"
                  aria-label="Back to Library"
                  onClick={() =>
                    context?.handleBreadcrumbClick(
                      parent.id,
                      parent.entityType,
                      parent.entityType === "LibraryFolderReference"
                        ? "folder"
                        : undefined
                    )
                  }
                >
                  {parent.entityType === "LibraryFolderReference" && (
                    <img src={shortcutIcon} />
                  )}
                  {parent.name}
                </button>
                <span className="grey-text bc-links-divider"> / </span>
              </>
            );
          })}
      </>
    );
  };

  const OrganizeOptns = () => {
    if (referenceId) {
      return (
        <div
          className="dropdown-menu organize-optns"
          style={{ display: "block", position: "absolute" }}
          ref={popUpRef}
        >
          <button
            type="button"
            className="organize-btn"
            onClick={() => setShowDeleteShortcutModal(true)}
          >
            <span className="optn">Delete shortcut</span>
          </button>
        </div>
      );
    }

    // Check if middle section has any visible actions (for hr separator logic)
    const hasMiddleActions =
      activeFolder?.canMove ||
      activeFolder?.canAddShortcut ||
      activeFolder?.canManageAccess;

    return (
      <div
        className="dropdown-menu dropdown-menu-right organize-optns"
        style={{ display: "block", position: "absolute" }}
        ref={popUpRef}
      >
        {activeFolder?.canRename && (
          <button
            type="button"
            className="organize-btn"
            onClick={() => setShowRenameModal(true)}
            style={{ paddingBottom: 14 }}
          >
            <span className="optn">Rename</span>
          </button>
        )}
        {activeFolder?.canRename &&
          (hasMiddleActions || activeFolder?.canDelete) && <hr />}
        {activeFolder?.canMove && (
          <button
            type="button"
            className="organize-btn"
            onClick={() => {
              if (activeFolder) {
                const srcEntity: SourceEntityToMoveType = {
                  id: activeFolder.id,
                  name: activeFolder.name,
                  type: "LibraryFolder",
                  parentFolderId: activeFolder.parentFolderId
                };
                setSrcEntity(srcEntity);
                setShowMovePopup(true);
              }
            }}
          >
            <span className="optn">Move this folder</span>
          </button>
        )}
        {activeFolder?.canAddShortcut && (
          <button
            type="button"
            className="organize-btn"
            onClick={() => {
              if (activeFolder)
                context?.toggleFolderFeatureModal(
                  true,
                  activeFolder.id,
                  activeFolder.name,
                  "LibraryFolder",
                  activeFolder.parentFolderId
                );
            }}
          >
            <span className="optn">Add shortcut</span>
            <span className="optn-info">Create a shortcut of this event</span>
          </button>
        )}
        {activeFolder?.canManageAccess && (
          <button
            type="button"
            className="organize-btn"
            onClick={() => {
              if (activeFolder) {
                context?.toggleManagePermissionsModal(
                  true,
                  "LibraryFolder",
                  activeFolder.id,
                  activeFolder.name
                );
                setShowOrganizeOptns(false);
              }
            }}
          >
            <span className="optn">Manage access</span>
            <span className="optn-info">Control who can view and edit</span>
          </button>
        )}
        {activeFolder?.canDelete &&
          (hasMiddleActions || activeFolder?.canRename) && <hr />}
        {activeFolder?.canDelete && (
          <button
            type="button"
            className="organize-btn"
            onClick={() => setShowDeleteModal(true)}
            style={{ paddingTop: 14 }}
          >
            <span className="optn">Delete</span>
          </button>
        )}
      </div>
    );
  };

  const onReNameClicked = async (
    folder: Folder | BasicFolderData,
    name: string
  ) => {
    await updateFolder(folder.id, name, folder.parentFolderId);
    setDisplayName(name);
    fetchFolderData(folder.id, folder.referenceId);
    context?.optimisticUpdateFolderName(folder.id, name);
  };

  const handleInlineRenameSubmit = async () => {
    if (activeFolder) {
      if (hasInvalidatedValues(true, entityNameDraft)) {
        return;
      }

      setIsSubmitting(true);

      try {
        await updateFolder(
          activeFolder.id,
          entityNameDraft,
          activeFolder.parentFolderId
        );

        setDisplayName(entityNameDraft);
        toggleEditMode(false, "");
        context?.optimisticUpdateFolderName(activeFolder.id, entityNameDraft);
        await fetchFolderData(activeFolder.id, referenceId);
      } catch (error) {
        console.error("Error updating folder name:", error);
        //@ts-ignore
        toastr.error("Failed to update folder name. Please try again");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const onDeleteFolder = async (folder: Folder | BasicFolderData) => {
    await context?.onDeleteFolder(folder);
    setShowDeleteModal(false);

    // Set flag to skip auto-expansion during navigation after deletion
    context?.setNavigatingAfterDeletion?.(true);

    // Redirect to parent folder after deletion
    if (folder.parentFolderId) {
      context?.handleBreadcrumbClick(folder.parentFolderId, "LibraryFolder");
    } else {
      context?.navigateToRoot?.();
      window.history.pushState({}, "", "/library");
      context?.setSelectedView(null);
    }
  };

  const onDeleteShortCut = async (shortCut: ShortCutType) => {
    setShowDeleteShortcutModal(false);

    // Refresh entire folder tree to ensure shortcut is removed from all locations
    await context?.refreshFolderTree();
  };

  const onMovingSuccess = (val: {
    shouldReloadTreeView: boolean;
    src: any;
    destination: any;
  }) => {
    setShowMovePopup(false);

    // Redirect to parent folder after moving
    if (srcEntity?.parentFolderId) {
      context?.handleBreadcrumbClick(srcEntity.parentFolderId, "LibraryFolder");
    } else {
      context?.navigateToRoot?.();
      window.history.pushState({}, "", "/library");
      context?.setSelectedView(null);
    }

    // Update sidebar - always reload source folder data (where folder was moved from)
    if (srcEntity?.parentFolderId) {
      context?.reloadFolderData(
        "nested",
        srcEntity.id,
        srcEntity.parentFolderId
      );
    } else {
      context?.reloadFolderData("root", srcEntity.id, null);
    }

    // Update destination folder in sidebar (conditionally if expanded)
    if (
      val.destination?.entityId &&
      context?.isExpandedFolder?.(val.destination.entityId)
    ) {
      context?.reloadFolderData(
        "nested",
        val.destination.entityId,
        val.destination.entityId
      );
    } else if (val.destination?.entityId === null) {
      // Moving to root - reload root folders
      context?.reloadFolderData("root", 0, null);
    }

    if (val.shouldReloadTreeView) {
      context?.reloadFolderData("root", srcEntity.id, null);
    }
  };

  return (
    <>
      <div className="ws-breadcrumbs ws-action-and-breadcrumbs">
        <div className="album-breadcrumb-left-content folder-breadcrumb ws-txtarea-width">
          <p className="ws-head-txt">
            <button
              type="button"
              className="green-text geo-text-btn"
              aria-label="Back to Library"
              onClick={handleBreadcrumbClick}
            >
              <img src={GreenArrow} alt="Back" style={{ marginRight: 5 }} />
              Library
            </button>
            <span className="grey-text bc-links-divider"> / </span>
            {renderParentLinks()}
            {isLoadingFolder ? (
              <span className="loading-spinner">
                <span className="fa fa-spinner fa-spin"></span>
              </span>
            ) : (
              <span
                className="grey-text grey-text-limit-w"
                data-toggle="tooltip"
                data-placement="bottom"
                data-original-title={activeFolder?.name ?? ""}
              >
                {activeFolder?.isReference && <img src={shortcutIcon} />}
                <span className="text-truncate">{activeFolder?.name}</span>
              </span>
            )}
          </p>
          {isLoadingFolder ? (
            <div className="loading-spinner event-name-spinner">
              <span className="fa fa-spinner fa-spin"></span>
            </div>
          ) : isInEditMode ? (
            <div
              className="ws-edit-album-name ws-edit-section-padding black-text"
              onClick={e => e.stopPropagation()}
            >
              <textarea
                defaultValue={displayName || activeFolder?.name}
                onChange={updateDraftName}
                onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) =>
                  handleKeyDownAction(e, handleInlineRenameSubmit)
                }
                onClick={e => e.stopPropagation()}
                autoFocus
                onFocus={e => {
                  const val = e.target.value;
                  e.target.value = "";
                  e.target.value = val;
                }}
              />
              <div className="ws-edit-album-actions ws-breadrumb-edit-actions">
                {isSubmitting ? (
                  <span
                    className="fa fa-spinner fa-spin"
                    style={{ marginRight: 5 }}
                  ></span>
                ) : (
                  <>
                    <img
                      className="actionIcon"
                      src={submitIcon}
                      onClick={e =>
                        submitNameChange(e, handleInlineRenameSubmit)
                      }
                      style={{ cursor: "pointer", opacity: 1 }}
                    />
                    <img
                      className="actionIcon"
                      style={{ marginLeft: 5, cursor: "pointer", opacity: 1 }}
                      src={cancelIcon}
                      onClick={e =>
                        toggleEditMode(false, activeFolder?.name || "", e)
                      }
                    />
                  </>
                )}
              </div>
            </div>
          ) : (
            <span className="black-text ws-folder-name-div">
              {activeFolder?.isReference && <img src={shortcutIcon} />}
              <span className="ws-edit-name-h3">
                {displayName || activeFolder?.name}
              </span>
              {!referenceId && activeFolder?.canRename && (
                <span
                  className="ws-edit-name-icon ws-breadcrumb-edit-icon"
                  data-toggle="tooltip"
                  data-placement="top"
                  data-container="body"
                  title="Edit name"
                  id={`edit-icon-${activeFolder?.id}`}
                  onMouseEnter={e =>
                    context?.showActionTooltip(
                      activeFolder?.id || null,
                      "Edit name",
                      "edit"
                    )
                  }
                  onClick={e =>
                    toggleEditMode(true, activeFolder?.name || "", e)
                  }
                >
                  <img src={editIcon} alt="Edit folder name" />
                </span>
              )}
            </span>
          )}
        </div>
        <div className="ws-album-actions-main ws-album-actions-folder">
          <div className="ws-mobile-sub-header">
            {/* Show Organize button if reference (can delete shortcut) or if any action is available */}
            {(referenceId ||
              activeFolder?.canRename ||
              activeFolder?.canMove ||
              activeFolder?.canAddShortcut ||
              activeFolder?.canManageAccess ||
              activeFolder?.canDelete) && (
              <button
                className="geo-btn geo-btn--small geo-btn--secondary geo-btn--caps ws-organize-btn"
                onClick={e => {
                  setShowOrganizeOptns(!showOrganizeOptns);
                }}
              >
                Organise
                <img
                  id="ws-arrow-down-icon"
                  src={arrowDownIcon}
                  alt="Uploader-options"
                />
                {showOrganizeOptns && <OrganizeOptns />}
              </button>
            )}
            <CreateDropdown
              onGalleryClick={handleGalleryClick}
              onFolderClick={handleFolderClick}
              canCreateEvents={activeFolder?.canCreateEvents}
              canCreateFolders={activeFolder?.canCreateFolders}
            />
          </div>
        </div>
      </div>
      {showRenameModal && (
        <Rename
          onClose={() => setShowRenameModal(false)}
          folder={basicFolderData}
          onReNameClicked={onReNameClicked}
        />
      )}
      {showDeleteModal && (
        <Delete
          onClose={() => setShowDeleteModal(false)}
          folder={basicFolderData}
          onDeleteFolder={onDeleteFolder}
        />
      )}
      {showDeleteShortcutModal && referenceId && folder && (
        <DeleteShortcut
          onClose={() => setShowDeleteShortcutModal(false)}
          shortCut={folder}
          onDeleteShortCut={onDeleteShortCut}
          referenceId={parseInt(referenceId)}
          type="folder"
        />
      )}
      {showMovePopup && srcEntity && (
        <MoveToFolderPopup
          onClose={() => setShowMovePopup(false)}
          srcEntity={srcEntity}
          onMovingSuccess={onMovingSuccess}
          referenceId={referenceId}
        />
      )}
    </>
  );
};

export default FolderViewBreadcrumb;
